<template>
    <TransitionRoot as="template" :show="open">
        <Dialog as="div" static class="fixed inset-0 z-30 overflow-hidden" @close="open = false" :open="open">
            <div class="absolute inset-0 overflow-hidden">
                <DialogOverlay class="absolute inset-0" />

                <div class="fixed inset-y-0 right-0 pl-10 max-w-full flex">
                    <TransitionChild as="template" enter="transform transition ease-in-out duration-300" enter-from="translate-x-full" enter-to="translate-x-0" leave="transform transition ease-in-out duration-300" leave-from="translate-x-0" leave-to="translate-x-full">
                        <div class="w-screen max-w-md">
                            <div class="h-full flex flex-col py-6 bg-white shadow-xl overflow-y-scroll">
                                <div class="px-4 sm:px-6">
                                    <div class="flex items-start justify-between">
                                        <DialogTitle class="text-lg font-medium text-gray-900">
                                            {{ __('admin::files.files') }}
                                        </DialogTitle>
                                        <div class="ml-3 h-7 flex items-center">
                                            <button class="bg-white rounded-md text-gray-400 hover:text-gray-500" @click="open = false">
                                                <span class="sr-only">Close panel</span>
                                                <XIcon class="h-6 w-6" aria-hidden="true" />
                                            </button>
                                        </div>
                                    </div>

                                </div>
                                <div class="mt-6 relative flex-1 px-4 sm:px-6">

                                    <div class="relative">
                                        <input
                                            type="file"
                                            id="fileUpload"
                                            class="absolute inset-0 z-50 m-0 p-0 w-full h-full outline-none opacity-0 cursor-pointer"
                                            v-on:dragover="adding = true"
                                            v-on:dragleave="adding = false"
                                            v-on:drop="adding = false"
                                            ref="file"
                                            @input="handleFileUpload"
                                        >
                                        <label for="fileUpload">
                                            <div
                                                class="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md"
                                                :class="{ 'bg-indigo-100' : adding }"
                                            >

                                                <div class="space-y-1 text-center">

                                                    <div class="w-100 h-14">

                                                        <svg v-if="uploading" class="animate-spin mx-auto h-12 w-12 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                                        </svg>

                                                        <svg v-else class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                                                            <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                                                        </svg>
                                                    </div>

                                                    <div class="flex text-sm text-gray-600">
                                                        <p class="font-medium text-indigo-600">
                                                            {{ __('forms::labels.upload_a_file') }}
                                                        </p>
                                                        <p class="pl-1">{{ __('forms::labels.or_drag_and_drop').toLowerCase() }}</p>
                                                    </div>
                                                    <p class="text-xs text-gray-500">
                                                        {{ __('forms::labels.all_files_allowed') }}
                                                    </p>

                                                </div>
                                            </div>
                                        </label>
                                    </div>

                                    <div class="pt-8" v-if="! initiated">
                                        <svg class="animate-spin h-8 w-8 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                                            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                    </div>

                                    <div class="pt-8 grid grid-cols-2 gap-2" v-if="initiated">

                                        <div
                                            class="h-32 w-full rounded bg-gray-100 border border-gray-200 bg-center bg-cover bg-no-repeat relative cursor-pointer"
                                            v-bind:style="{ backgroundImage: 'url(' + file.path + ')' }"
                                            @click="select(file)"
                                            v-for="file in files"
                                            :key="file.id"
                                        >
                                            <CheckIcon v-if="file.id === selectedId" class="absolute z-10 text-green-500 w-6 h-6 top-2 right-2" />
                                            <div class="absolute px-1 py-1 w-full bg-opacity-75 bg-white text-xs text-gray-900 bottom-0">
                                                <div class="line-clamp-1 break-all">{{ file.name }}</div>
                                            </div>
                                        </div>

                                    </div>

                                </div>
                            </div>
                        </div>
                    </TransitionChild>
                </div>
            </div>
        </Dialog>
    </TransitionRoot>
</template>

<script>
import eventBus from '@S/eventBus'
import axios from 'axios'

import {
    CheckIcon,
    XIcon,
} from '@heroicons/vue/outline'

import { Dialog, DialogOverlay, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue'

export default {
    components: {
        Dialog,
        DialogOverlay,
        DialogTitle,
        TransitionChild,
        TransitionRoot,
        CheckIcon,
        XIcon,
    },
    data() {
        return {
            initiated: false,
            open: false,
            name: null,
            adding: false,
            uploading: false,
            selectedId: null,
            files: [],
        };
    },
    mounted() {
        eventBus.$on('open-file-selector', (name, selectedId) => {
            this.loadFiles();
            this.name = name;
            this.selectedId = selectedId;
            this.open = true;
        });
    },
    methods: {
        loadFiles() {
            if (! this.initiated) {

                axios
                    .get(this.$route('admin::files.index'))
                    .then((res) => {
                        this.files = res.data;
                        this.initiated = true;
                    });
            }
        },
        handleFileUpload(event) {
            this.uploading = true;
            let file = event.target.files[0];

            let request = new FormData();
            request.append('file', file);

            axios
                .post(this.$route('admin::files.store'), request, {
                    header: {
                        'Content-Type': 'multipart/form-data'
                    },
                })
                .then((res) => {
                    this.uploading = false;
                    this.files.unshift(res.data);
                });
        },
        select(file) {
            eventBus.$emit('file-selected:' + this.name, {
                id: file.id,
                name: file.name,
            });
            this.selectedId = file.id;
            this.open = false;
        },
    },
}
</script>
